/**
 * Renders an interactive (or editable) user message.
 */
import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import { getFontSize, getActiveModule } from "../../store";
import { PencilIcon, CheckboxIcon } from "../../lib/icons";
import { deepChange, goToModule } from "../../actions";
import { getEditingMessageNum, getErrorMessage } from "./selectors";
import type { GlobalState } from "../../lib/types";

/**
 * User Interactive Message component.
 */
class InteractiveUserMessage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      key: props.messageNum,
    };
  }

  /**
   * Editable message click handler.
   */
  initEditing = () => {
    const { text, dispatch, fromHistory, activeModule, submitted, activeModuleId } = this.props;
    const { key } = this.state;
    if (fromHistory || submitted) {
      return;
    }

    dispatch(
      deepChange({
        textInputValue: text,
        editingMessageNum: +key,
        workWithHistory: true,
        textBeforeEditing: text,
        moduleIDBeforeEditing: activeModule.id,
      })
    );
    dispatch(goToModule(activeModuleId, { quiet: true }));

    setTimeout(() => {
      window.famInputField && window.famInputField.focus();
    }, 50);
  };

  render() {
    const { text, fontSize, errorMessage, fromHistory, submitted } = this.props;
    const showError = Boolean(errorMessage);
    const errorComponent = showError ? <ErrorMessage>{errorMessage}</ErrorMessage> : null;

    let icon = showError ? <PencilIcon fillColor="#FF004C" fillOpacity="0.5" /> : <PencilIcon />;
    let textClass = "text";
    if (submitted) {
      icon = <CheckboxIcon />;
      textClass += " not-allowed";
    }

    return (
      <MessageContainer>
        <ResponseWrap className={fromHistory ? "inactive" : ""}>
          <Message
            fontSize={fontSize}
            border={showError ? "#ffe8ef" : "#eaeff6"}
            color={showError ? "#ff004c" : "#000000"}
          >
            <div className={textClass} onClick={this.initEditing}>
              {text}
              <IconWrap>{icon}</IconWrap>
            </div>
          </Message>
        </ResponseWrap>
        {errorComponent}
      </MessageContainer>
    );
  }
}

const MessageContainer = styled.div`
  width: 100%;
`;

const ResponseWrap = styled.div`
  left: 5.6%;
  right: 46.93%;
  top: 23.28%;
  bottom: 69.95%;
  display: inline-block;
  margin-bottom: 16px;
  max-width: calc(100% - 40px);
  text-align: left;

  &.inactive .text {
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid ${(props) => props.border};
  box-sizing: border-box;
  border-radius: 12px;
  padding: 15px 21px 16px 26px;
  line-height: 20px;
  .text {
    font-style: normal;
    font-weight: 800;
    font-size: ${(props) => props.fontSize || "18px"};
    line-height: 25px;
    color: ${(props) => props.color};
    mix-blend-mode: normal;
    cursor: pointer;

    &.not-allowed {
      cursor: not-allowed;
    }
  }
`;

const IconWrap = styled.span`
  padding-left: 15px;
`;

export const ErrorMessage = styled.div`
  padding-bottom: 11px;
  width: 275px;
  font-style: normal;
  font-weight: normal;
  font-size: 10px;
  line-height: 12px;
  color: #ff004c;
`;

/**
 * Map state to props.
 */
function mapStateToProps(state: GlobalState, ownProps) {
  const { messageNum } = ownProps;
  return {
    fontSize: getFontSize(state),
    text: ownProps.text,
    type: ownProps.type,
    action: ownProps.action,
    answers: ownProps.answers,
    timestamp: ownProps.timestamp,
    fromHistory: ownProps.fromHistory,
    submitted: ownProps.submitted,
    activeModule: getActiveModule(state),
    messageNum,
    errorMessage: getErrorMessage(messageNum, state),
    editingMessageNum: getEditingMessageNum(state),
  };
}

export default connect(mapStateToProps)(InteractiveUserMessage);
